import { useState, useRef, useEffect } from 'react';
import axiosInstance from '../../helpers/axios';
import botAvatar from '../../assets/images/avatar/bot_avtr.jpg';
import './ChatBot.css';

const generateSessionId = () => {
  return 'sess-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
};

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'bot', text: 'Hi! I can help you create a loan application. Please provide applicant details like name, mobile, email, course name, course fees, tenure, and institute name.' }
  ]);
  const [inputText, setInputText] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const sessionId = useRef(generateSessionId());
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text && attachedFiles.length === 0) return;

    const userMessage = { role: 'user', text, files: attachedFiles.map(f => f.name) };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    const filesToSend = [...attachedFiles];
    setAttachedFiles([]);
    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append('message', text || ' ');
      formData.append('sessionId', sessionId.current);
      filesToSend.forEach(file => formData.append('files', file));

      const response = await axiosInstance.post('/application/create-from-chat', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const data = response.data?.data;
      let botText = '';

      if (typeof data === 'string') {
        botText = data;
      } else if (data?.message) {
        botText = data.message;
      } else if (data?.response) {
        botText = data.response;
      } else if (data) {
        botText = typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data);
      } else {
        botText = response.data?.message || 'Done!';
      }

      setMessages(prev => [...prev, { role: 'bot', text: botText }]);
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Something went wrong. Please try again.';
      setMessages(prev => [...prev, { role: 'bot', text: errMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setAttachedFiles(prev => {
      const combined = [...prev, ...selected];
      return combined.slice(0, 6);
    });
    e.target.value = '';
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      {/* Floating bot icon */}
      <button
        className="chatbot-fab"
        onClick={() => setIsOpen(prev => !prev)}
        title="Chat with AI Assistant"
      >
        <img src={botAvatar} alt="Bot" className="chatbot-fab-img" />
      </button>

      {/* Overlay */}
      {isOpen && (
        <div className="chatbot-overlay" onClick={() => setIsOpen(false)} />
      )}

      {/* Sidebar panel */}
      <div className={`chatbot-panel ${isOpen ? 'chatbot-panel--open' : ''}`}>
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header-info">
            <img src={botAvatar} alt="Bot" className="chatbot-header-avatar" />
            <div>
              <div className="chatbot-header-title">Loan Application Helper</div>
            </div>
          </div>
          <button className="chatbot-close-btn" onClick={() => setIsOpen(false)}>&#10005;</button>
        </div>

        {/* Messages */}
        <div className="chatbot-messages">
          {messages.map((msg, idx) => (
            <div key={idx} className={`chatbot-message chatbot-message--${msg.role}`}>
              {msg.role === 'bot' && (
                <img src={botAvatar} alt="Bot" className="chatbot-msg-avatar" />
              )}
              <div className={`chatbot-bubble chatbot-bubble--${msg.role}`}>
                {msg.text && (
                  <span style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</span>
                )}
                {msg.files && msg.files.length > 0 && (
                  <div className="chatbot-bubble-files">
                    {msg.files.map((name, i) => (
                      <span key={i} className="chatbot-file-tag">📎 {name}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="chatbot-message chatbot-message--bot">
              <img src={botAvatar} alt="Bot" className="chatbot-msg-avatar" />
              <div className="chatbot-bubble chatbot-bubble--bot chatbot-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Attached files preview */}
        {attachedFiles.length > 0 && (
          <div className="chatbot-files-preview">
            {attachedFiles.map((file, i) => (
              <div key={i} className="chatbot-file-preview-item">
                <span className="chatbot-file-preview-name">📎 {file.name}</span>
                <button className="chatbot-file-remove" onClick={() => removeFile(i)}>&#10005;</button>
              </div>
            ))}
          </div>
        )}

        {/* Input area */}
        <div className="chatbot-input-area">
          <button
            className="chatbot-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Attach files (max 6)"
            disabled={attachedFiles.length >= 6}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
            </svg>
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            multiple
            onChange={handleFileChange}
          />
          <textarea
            className="chatbot-textarea"
            placeholder="Type your message..."
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={5}
            disabled={isLoading}
          />
          <button
            className="chatbot-send-btn"
            onClick={handleSend}
            disabled={isLoading || (!inputText.trim() && attachedFiles.length === 0)}
            title="Send"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  );
};

export default ChatBot;
